<template>
  <div class="min-h-screen pb-10">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <GoBackTitle title="Nouvelle facture proforma" :to="{ name: 'proforma-invoices' }" />
      <BaseButton variant="primary" @click="createProforma" :disabled="!canCreate || isProcessing"
        :loading="isProcessing">
        <template #icon-left>
          <i class="fa-solid fa-check"></i>
        </template>
        Créer la proforma
      </BaseButton>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Colonne principale -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Recherche produit + Panier -->
        <BaseCard class="!overflow-visible">
          <template #header>
            <div class="flex items-center justify-between w-full">
              <h3 class="text-lg font-semibold text-gray-900">Articles</h3>
              <button v-if="cart.length > 0" @click="clearCart"
                class="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <i class="fa-solid fa-trash"></i>
                Vider le panier
              </button>
            </div>
          </template>

          <!-- Barre de recherche produit -->
          <div class="relative mb-4" ref="productSearchContainer">
            <div class="relative">
              <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input v-model="productSearch" type="text" placeholder="Rechercher un produit..."
                class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                @input="searchProducts" @focus="showProductDropdown = true" @blur="hideProductDropdownDelayed" />
            </div>

            <!-- Dropdown produits -->
            <div
              v-if="showProductDropdown && (productSearching || productSearchResults.length > 0 || productSearch.length >= 2)"
              class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto z-50">
              <div v-if="productSearching" class="px-4 py-3 text-sm text-gray-500">
                <i class="fa-solid fa-spinner fa-spin mr-2"></i>Recherche...
              </div>
              <div v-else-if="productSearchResults.length === 0 && productSearch.length >= 2"
                class="px-4 py-3 text-sm text-gray-500">
                Aucun produit trouvé
              </div>
              <button v-for="product in productSearchResults" :key="product.id" type="button"
                @mousedown.prevent="addToCart(product)"
                class="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors flex justify-between items-center">
                <div class="flex-1">
                  <div class="font-medium text-gray-900">{{ product.name }}</div>
                  <div class="text-sm text-gray-500">{{ formatCurrency(product.price_excl_tax) }}</div>
                </div>
                <span class="ml-3 px-2.5 py-1 text-xs font-semibold rounded-full"
                  :class="product.stock_quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'">
                  {{ product.stock_quantity || 0 }}
                </span>
              </button>
            </div>
          </div>

          <!-- Tableau panier -->
          <div v-if="cart.length === 0" class="text-center py-12 text-gray-500">
            <i class="fa-solid fa-shopping-cart text-5xl mb-4 text-gray-300"></i>
            <p class="text-lg font-medium">Le panier est vide !!!</p>
            <p class="text-sm mt-1">Recherchez d'abord le produit que vous voulez ajouter.</p>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-gray-200">
                  <th class="text-left py-3 px-2 text-sm font-semibold text-gray-600">Produit</th>
                  <th class="text-center py-3 px-2 text-sm font-semibold text-gray-600 w-28">Qté</th>
                  <th class="text-right py-3 px-2 text-sm font-semibold text-gray-600 w-32">Prix HT</th>
                  <th class="text-right py-3 px-2 text-sm font-semibold text-gray-600 w-32">Total</th>
                  <th class="w-12"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in cart" :key="item.product_id"
                  class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td class="py-3 px-2">
                    <div class="font-medium text-gray-900">{{ item.name }}</div>
                    <div v-if="item.tax_rate > 0" class="text-xs text-gray-500">TVA {{ item.tax_rate }}%</div>
                  </td>
                  <td class="py-3 px-2">
                    <div class="flex items-center justify-center gap-1">
                      <button @click="updateQuantity(index, item.quantity - 1)"
                        class="w-7 h-7 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                        :disabled="item.quantity <= 1">
                        <i class="fa-solid fa-minus text-xs"></i>
                      </button>
                      <input type="number" v-model.number="item.quantity" min="1"
                        class="w-14 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        @change="updateCartTotals" />
                      <button @click="updateQuantity(index, item.quantity + 1)"
                        class="w-7 h-7 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                        <i class="fa-solid fa-plus text-xs"></i>
                      </button>
                    </div>
                  </td>
                  <td class="py-3 px-2 text-right text-sm text-gray-700">
                    {{ formatCurrency(item.price_excl_tax) }}
                  </td>
                  <td class="py-3 px-2 text-right font-semibold text-gray-900">
                    {{ formatCurrency(item.total) }}
                  </td>
                  <td class="py-3 px-2 text-center">
                    <button @click="removeFromCart(index)"
                      class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <i class="fa-solid fa-times"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </BaseCard>
      </div>

      <!-- Colonne latérale -->
      <div class="lg:col-span-1 space-y-6">
        <!-- Résumé de la vente -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Résumé</h3>
          </template>
          <div class="space-y-4">
            <div class="flex justify-between items-center py-2">
              <span class="text-gray-600 flex items-center gap-2">
                <i class="fa-solid fa-shopping-cart text-primary"></i>
                Total HT
              </span>
              <span class="font-semibold text-lg">{{ formatCurrency(subtotalExclTax) }}</span>
            </div>
            <div class="flex justify-between items-center py-2">
              <span class="text-gray-600">TVA</span>
              <span class="font-medium">{{ formatCurrency(totalTax) }}</span>
            </div>
            <div v-if="discount > 0" class="flex justify-between items-center py-2 text-red-600">
              <span class="flex items-center gap-2">
                <i class="fa-solid fa-percent"></i>
                Remise
              </span>
              <span class="font-medium">-{{ formatCurrency(discount) }}</span>
            </div>
            <div class="border-t border-gray-200 pt-4">
              <div class="flex justify-between items-center">
                <span class="text-lg font-bold text-gray-900">Total TTC</span>
                <span class="text-2xl font-bold text-primary">{{ formatCurrency(totalInclTax) }}</span>
              </div>
            </div>

            <!-- Remise -->
            <div class="pt-4 border-t border-gray-200">
              <div class="flex items-center gap-2 mb-2">
                <i class="fa-solid fa-percent text-primary"></i>
                <span class="text-sm font-medium text-gray-700">Remise globale</span>
              </div>
              <BaseInput v-model.number="discount" type="number" min="0" step="1" placeholder="0 FCFA"
                @input="updateCartTotals" />
            </div>
          </div>
        </BaseCard>

        <!-- Client (obligatoire) -->
        <BaseCard class="!overflow-visible">
          <template #header>
            <div class="flex items-center gap-2">
              <h3 class="text-lg font-semibold text-gray-900">Client</h3>
              <span class="text-red-500">*</span>
            </div>
          </template>
          <div class="space-y-4">
            <!-- Tabs type client -->
            <div class="flex border-b border-gray-200">
              <button @click="customerType = 'existing'"
                class="flex-1 py-2 text-sm font-medium border-b-2 transition-colors"
                :class="customerType === 'existing' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'">
                Existant
              </button>
              <button @click="customerType = 'new'" class="flex-1 py-2 text-sm font-medium border-b-2 transition-colors"
                :class="customerType === 'new' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'">
                Nouveau
              </button>
            </div>

            <!-- Client existant -->
            <div v-if="customerType === 'existing'">
              <div v-if="selectedCustomer"
                class="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div class="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <i class="fa-solid fa-user text-primary"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 truncate">{{ selectedCustomer.name }}</div>
                  <div class="text-sm text-gray-500 truncate">{{ selectedCustomer.phone || selectedCustomer.email || '-'
                    }}
                  </div>
                </div>
                <button @click="clearCustomer" class="text-gray-400 hover:text-red-500 p-1">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>

              <div v-else class="relative" ref="customerSearchContainer">
                <div class="relative">
                  <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input v-model="customerSearch" type="text" placeholder="Rechercher un client..."
                    class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    @input="searchCustomers" @focus="showCustomerDropdown = true" @blur="hideCustomerDropdownDelayed" />
                </div>

                <!-- Dropdown clients -->
                <div
                  v-if="showCustomerDropdown && (customersLoading || customers.length > 0 || customerSearch.length >= 2)"
                  class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                  <div v-if="customersLoading" class="px-4 py-3 text-sm text-gray-500">
                    <i class="fa-solid fa-spinner fa-spin mr-2"></i>Recherche...
                  </div>
                  <div v-else-if="customers.length === 0 && customerSearch.length >= 2"
                    class="px-4 py-3 text-sm text-gray-500">
                    Aucun client trouvé
                  </div>
                  <button v-for="customer in customers" :key="customer.id" type="button"
                    @mousedown.prevent="selectCustomer(customer)"
                    class="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors">
                    <div class="font-medium text-gray-900">{{ customer.name }}</div>
                    <div class="text-sm text-gray-500">{{ customer.phone || 'Pas de téléphone' }}</div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Nouveau client -->
            <div v-if="customerType === 'new'" class="space-y-3">
              <BaseInput v-model="newCustomer.name" placeholder="Nom du client *" required />
              <BaseInput v-model="newCustomer.phone" placeholder="Téléphone" />
            </div>

            <!-- Message d'erreur si pas de client -->
            <div v-if="!hasValidCustomer" class="text-sm text-amber-600 flex items-center gap-2">
              <i class="fa-solid fa-exclamation-triangle"></i>
              <span>Client obligatoire</span>
            </div>
          </div>
        </BaseCard>

        <!-- Informations supplémentaires -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Informations</h3>
          </template>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Date de validité</label>
              <BaseInput v-model="validityDate" type="date" :min="minValidityDate" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea v-model="notes" rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                placeholder="Notes internes..."></textarea>
            </div>
          </div>
        </BaseCard>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { proformaInvoiceService, productsService, customerService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import GoBackTitle from '@/components/base/GoBackTitle.vue'

export default defineComponent({
  name: 'CreateProformaInvoiceView',
  components: {
    BaseCard,
    BaseButton,
    BaseInput,
    GoBackTitle
  },
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    // État
    const isProcessing = ref(false)
    const customerType = ref('existing')
    const customerSearch = ref('')
    const customersLoading = ref(false)
    const selectedCustomerId = ref(null)
    const selectedCustomer = ref(null)
    const customers = ref([])
    const showCustomerDropdown = ref(false)
    const newCustomer = reactive({
      name: '',
      phone: ''
    })
    const cart = ref([])
    const discount = ref(0)
    const validityDate = ref('')
    const notes = ref('')

    // Recherche produits
    const productSearch = ref('')
    const productSearchResults = ref([])
    const productSearching = ref(false)
    const showProductDropdown = ref(false)
    const searchTimeout = ref(null)

    // Computed
    const hasValidCustomer = computed(() => {
      if (customerType.value === 'existing') {
        return !!selectedCustomerId.value
      }
      if (customerType.value === 'new') {
        return !!newCustomer.name.trim()
      }
      return false
    })

    const hasValidCart = computed(() => {
      return cart.value.length > 0
    })

    const subtotalExclTax = computed(() => {
      return cart.value.reduce((sum, item) => sum + (item.price_excl_tax * item.quantity), 0)
    })

    const totalTax = computed(() => {
      return cart.value.reduce((sum, item) => {
        const itemTax = (item.price_excl_tax * item.quantity * item.tax_rate) / 100
        return sum + itemTax
      }, 0)
    })

    const totalInclTax = computed(() => {
      const total = subtotalExclTax.value + totalTax.value
      return Math.max(0, total - discount.value)
    })

    const minValidityDate = computed(() => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    })

    const canCreate = computed(() => {
      return hasValidCustomer.value && hasValidCart.value && !isProcessing.value
    })

    // Méthodes
    const hideProductDropdownDelayed = () => {
      setTimeout(() => {
        showProductDropdown.value = false
      }, 200)
    }

    const hideCustomerDropdownDelayed = () => {
      setTimeout(() => {
        showCustomerDropdown.value = false
      }, 200)
    }

    const searchProducts = async () => {
      if (!productSearch.value || productSearch.value.length < 2) {
        productSearchResults.value = []
        return
      }

      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(async () => {
        try {
          productSearching.value = true
          const response = await productsService.getProducts({
            search: productSearch.value,
            per_page: 10
          })
          productSearchResults.value = response.data?.data || response.data || []
        } catch (error) {
          console.error('Erreur lors de la recherche de produits:', error)
          productSearchResults.value = []
        } finally {
          productSearching.value = false
        }
      }, 300)
    }

    const addToCart = (product) => {
      // Vérifier si le produit est déjà dans le panier
      const existingIndex = cart.value.findIndex(item => item.product_id === product.id)

      if (existingIndex >= 0) {
        // Incrémenter la quantité
        cart.value[existingIndex].quantity++
        updateCartTotals()
      } else {
        // Ajouter au panier
        const itemTotalExclTax = product.price_excl_tax
        const itemTax = (itemTotalExclTax * (product.tax_rate || 0)) / 100

        cart.value.push({
          product_id: product.id,
          name: product.name,
          price_excl_tax: product.price_excl_tax,
          tax_rate: product.tax_rate || 0,
          quantity: 1,
          total: itemTotalExclTax + itemTax
        })
      }

      // Réinitialiser la recherche
      productSearch.value = ''
      productSearchResults.value = []
      showProductDropdown.value = false
    }

    const removeFromCart = (index) => {
      cart.value.splice(index, 1)
    }

    const clearCart = () => {
      cart.value = []
    }

    const updateQuantity = (index, newQuantity) => {
      if (newQuantity < 1) return
      cart.value[index].quantity = newQuantity
      updateCartTotals()
    }

    const updateCartTotals = () => {
      cart.value.forEach(item => {
        const itemTotalExclTax = item.price_excl_tax * item.quantity
        const itemTax = (itemTotalExclTax * item.tax_rate) / 100
        item.total = itemTotalExclTax + itemTax
      })
    }

    const searchCustomers = async () => {
      if (!customerSearch.value || customerSearch.value.length < 2) {
        customers.value = []
        return
      }

      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(async () => {
        try {
          customersLoading.value = true
          const response = await customerService.getCustomers({
            search: customerSearch.value,
            per_page: 10
          })
          customers.value = response.data?.data || response.data || []
        } catch (error) {
          console.error('Erreur lors de la recherche de clients:', error)
        } finally {
          customersLoading.value = false
        }
      }, 300)
    }

    const selectCustomer = (customer) => {
      selectedCustomerId.value = customer.id
      selectedCustomer.value = customer
      customers.value = []
      customerSearch.value = ''
      showCustomerDropdown.value = false
    }

    const clearCustomer = () => {
      selectedCustomerId.value = null
      selectedCustomer.value = null
      customerSearch.value = ''
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const createProforma = async () => {
      if (!canCreate.value || isProcessing.value) return

      isProcessing.value = true

      try {
        // Préparer les items
        const items = cart.value.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price_excl_tax: item.price_excl_tax
        }))

        // Préparer les données
        const proformaData = {
          items,
          discount: discount.value || 0
        }

        // Ajouter le client
        if (customerType.value === 'existing' && selectedCustomerId.value) {
          proformaData.customer_id = selectedCustomerId.value
        } else if (customerType.value === 'new' && newCustomer.name) {
          proformaData.customer = {
            name: newCustomer.name,
            phone: newCustomer.phone || null
          }
        }

        // Ajouter les informations supplémentaires
        if (validityDate.value) {
          proformaData.validity_date = validityDate.value
        }
        if (notes.value) {
          proformaData.notes = notes.value
        }

        // Créer la proforma
        await proformaInvoiceService.createProformaInvoice(proformaData)

        toastStore.addToast({
          type: 'success',
          message: 'Facture proforma créée avec succès',
          duration: 4000
        })

        // Rediriger vers la liste
        router.push({ name: 'proforma-invoices' })
      } catch (error) {
        console.error('Erreur lors de la création de la proforma:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de la création de la proforma',
          duration: 4000
        })
      } finally {
        isProcessing.value = false
      }
    }

    return {
      isProcessing,
      customerType,
      customerSearch,
      customersLoading,
      selectedCustomerId,
      selectedCustomer,
      customers,
      showCustomerDropdown,
      newCustomer,
      cart,
      discount,
      validityDate,
      notes,
      productSearch,
      productSearchResults,
      productSearching,
      showProductDropdown,
      hasValidCustomer,
      hasValidCart,
      subtotalExclTax,
      totalTax,
      totalInclTax,
      minValidityDate,
      canCreate,
      hideProductDropdownDelayed,
      hideCustomerDropdownDelayed,
      searchProducts,
      addToCart,
      removeFromCart,
      clearCart,
      updateQuantity,
      updateCartTotals,
      searchCustomers,
      selectCustomer,
      clearCustomer,
      formatCurrency,
      createProforma
    }
  }
})
</script>

<style scoped>
/* Masquer les flèches des inputs number */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>